import { SupportChannelAPI } from '@business/api/SupportChannel';
import {
  ChannelAssignId,
  ChannelAssignStatusEnum,
  ChannelId,
  SupportChannelAssginRequest,
  SupportChannelAssginResponse,
} from '@business/entities/SupportChannel';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { R } from '@core/utils/r';
import { Repository } from '@core/utils/repository';
import { showConfirmation, showDeleteConfirmation } from '@modules/common';
import { map, switchMap } from 'rxjs/operators';
import { ChannelEmailVerifyRequest } from './../../entities/SupportChannel/SupportChannelAssginResponse';
import { SMTPFormData } from '@modules/settings/SettingsPage/Components/SupportTab/ChannelTab/ChannelEmailTab/EmailSettingsModal/EmailSettingsModal';

export class SupportChannelAssignBloc extends BaseBloc {
  private readonly assignRepo = new Repository<SupportChannelAssginResponse>({
    getItemId: item => item.id,
  });

  readonly assigns$ = this.assignRepo.items$;
  readonly selectItem = this.assignRepo.selectItem;
  readonly channelAssigns = (channelId: ChannelId) => {
    return this.assigns$.pipe(map(items => R.filter(items, i => i.channelId == channelId)));
  };

  readonly verifiedAssigns$ = this.assigns$.pipe(
    map(items =>
      R.orderBy(
        R.filter(items, i => i.status == ChannelAssignStatusEnum.verified || i.channelId == '0'),
        'domain',
        'asc',
      ),
    ),
  );

  onReset() {
    this.assignRepo.reset();
  }

  fetchChannelAssgins = (channelId: ChannelId) => {
    return SupportChannelAPI.fetchChannelAssgins(channelId).pipe(
      this.assignRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_channels_error') }),
    );
  };

  fetchVerifiedEmails = () => {
    return SupportChannelAPI.fetchVerifiedEmails().pipe(
      this.assignRepo.ops.upsertMany(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_channels_error') }),
    );
  };

  createChannelAssgin = (channelId: ChannelId, data: SupportChannelAssginRequest) => {
    return SupportChannelAPI.createChannelAssgin(channelId, data).pipe(
      this.assignRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_channel'),
      }),
    );
  };

  updateChannelAssgin = (id: ChannelAssignId, data: SupportChannelAssginRequest) => {
    return SupportChannelAPI.updateChannelAssgin(id, data).pipe(
      this.assignRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_channel'),
      }),
    );
  };
  fetchChannelAssign = (id: ChannelAssignId) => {
    return SupportChannelAPI.fetchChannelAssgin(id).pipe(
      this.assignRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_email_senders') }),
    );
  };

  validateDomain = (id: ChannelAssignId, data?: ChannelEmailVerifyRequest) => {
    return SupportChannelAPI.validateDomain(id, data).pipe(
      this.assignRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('validate_domain_dns'),
      }),
    );
  };
  createDomain = (id: ChannelAssignId) => {
    return SupportChannelAPI.createDomain(id).pipe(this.assignRepo.ops.upsertOne(item => ({ item })));
  };

  configureSMTP = (id: ChannelAssignId, data: SMTPFormData) => {
    return SupportChannelAPI.configureSMTP(id, data).pipe(this.assignRepo.ops.upsertOne(item => ({ item })));
  };

  deleteChannelAssgin = (id: ChannelAssignId) => {
    return showDeleteConfirmation('Delete Email', 'Do you really want to remove this channel email?').pipe(
      switchMap(() => SupportChannelAPI.deleteChannelAssgin(id)),
      this.assignRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_channel'),
      }),
    );
  };

  makeDefault = (id: ChannelAssignId) => {
    return showConfirmation(
      'Default Email Sender',
      'Are you sure that you want to make this the default email address for sending all emails from the GrandCentr.al system?',
    ).pipe(
      switchMap(() => SupportChannelAPI.makeDefault(id)),
      this.assignRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('update_email_sender'),
      }),
    );
  };
}
